import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Table from "@/components/Base/Table";
import Button from "@/components/Base/Button";
import { Dialog, Popover } from "@/components/Base/Headless";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Pagination from "@/components/Base/Pagination";
import Lucide from "@/components/Base/Lucide";
import Litepicker from "@/components/Base/Litepicker";
import { getToken } from "@/utils/auth";

/** Rebate Ledger 2.0 — simplified UI (see README-VERSIONS.txt; v1.0 backup: RebateLedgerPage.v1.0.backup.tsx). */

type Distributor = { id: string; name: string; supplyTo?: string[] };
type Team = { id: string; name: string; status?: boolean };
type Product = { id: string; name: string; sku?: string | null };
type LedgerRow = {
  id: string;
  accrualDate: string;
  status: string;
  qty: number;
  claimedQty: number;
  outstandingQty: number;
  rebatePerUnitSnapshot: string;
  rebateTotalSnapshot: string;
  claimedAmount: string;
  outstandingAmount: string;
  distributor: { id: string; name: string; code?: string | null };
  team?: { id: string; name: string } | null;
  product: { id: string; name: string; sku?: string | null; type?: string | null };
  stockInReceipt?: { id: string; batchNo?: string | null; invoiceNo?: string | null; refNo?: string | null } | null;
  tradeInDevice?: string | null;
};
type ClaimDocRow = {
  id: string;
  claimRef: string;
  claimDate: string;
  status: string;
  lineCount: number;
  totalClaimedAmount: string;
  settledAmount: string;
  outstandingAmount: string;
  externalInvoiceRef?: string | null;
  externalInvoiceDate?: string | null;
  settlementNote?: string | null;
  distributor: { id: string; name: string; code?: string | null };
};

type ClaimLineDetail = {
  id: string;
  claimedQty: number;
  claimedAmount: string;
  accrualQty: number;
  rebatePerUnit: string;
  product: { id: string; name: string; sku?: string | null };
  team?: { id: string; name: string } | null;
  stockInReceipt?: { id: string; batchNo?: string | null } | null;
};

type ClaimDetailPayload = {
  id: string;
  claimRef: string;
  claimDate: string;
  status: string;
  notes?: string | null;
  distributor: { id: string; name: string; code?: string | null };
  lines: ClaimLineDetail[];
};

function safeClaimExportBasename(claimRef: string) {
  return (claimRef || "rebate-claim").replace(/[^\w.-]+/g, "_").slice(0, 80);
}

/** Parse API money string/number (handles commas, odd whitespace). */
function parseMoneyAmount(v: string | number | undefined | null): number {
  if (v === undefined || v === null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const n = Number(String(v).trim().replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function claimRowAllowsDelete(c: ClaimDocRow) {
  const settled = parseMoneyAmount(c.settledAmount);
  if (settled > 0) return false;
  if (c.status === "SETTLED" || c.status === "PARTIALLY_SETTLED") return false;
  return true;
}

/** Submitted ↔ Paid (and partial → either) — not for cancelled claims. */
function claimRowAllowsPaymentStatusModal(c: ClaimDocRow) {
  return c.status !== "CANCELLED";
}

function formatClaimDocStatus(status: string) {
  if (status === "SETTLED") return "Paid";
  if (status === "SUBMITTED") return "Submitted";
  return status;
}

function fmtDate(v: string) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(d);
}

function to2(v: string | number) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

/** RM-style amount with thousands separators and 2 decimals. */
function to2Comma(n: number) {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "0.00";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
}

function dateToYMD(v: Date) {
  return v.toISOString().slice(0, 10);
}

function parseDateRangeInput(value: string): { startDate: string; endDate: string } | null {
  const m = value.trim().match(/(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  return { startDate: m[1], endDate: m[2] };
}

const REBATE_LEDGER_PRODUCT_FILTER_KEY = "rebateLedgerSelectedProductIds";

/** Reads the last-remembered product filter selection from localStorage (returns null if unset/invalid). */
function loadStoredProductIds(): string[] | null {
  try {
    const raw = localStorage.getItem(REBATE_LEDGER_PRODUCT_FILTER_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : null;
  } catch {
    return null;
  }
}

function saveStoredProductIds(ids: string[]) {
  try {
    localStorage.setItem(REBATE_LEDGER_PRODUCT_FILTER_KEY, JSON.stringify(ids));
  } catch {
    // ignore storage errors (e.g. private browsing quota)
  }
}

function StepHeading({ step, children, className }: { step: number; children: ReactNode; className?: string }) {
  return (
    <div className={["font-medium text-sm flex items-center gap-2", className].filter(Boolean).join(" ")}>
      <span
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-slate-100 text-xs font-semibold tabular-nums text-slate-800 dark:border-darkmode-400 dark:bg-darkmode-700 dark:text-slate-100"
        aria-hidden
      >
        {step}
      </span>
      <span>{children}</span>
    </div>
  );
}

export default function RebateLedgerPage() {
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();
  const today = new Date();
  const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const defaultStart = dateToYMD(monthStart);
  const defaultEnd = dateToYMD(today);

  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  const [distributorId, setDistributorId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [productFilterInitialized, setProductFilterInitialized] = useState(false);
  const [status, setStatus] = useState("");
  const [dateRange, setDateRange] = useState(`${defaultStart} - ${defaultEnd}`);

  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [claimQtyById, setClaimQtyById] = useState<Record<string, string>>({});
  const [claimRef, setClaimRef] = useState("");
  const [claimDate, setClaimDate] = useState(new Date().toISOString().slice(0, 10));
  const [claimNote, setClaimNote] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimDocs, setClaimDocs] = useState<ClaimDocRow[]>([]);
  const [expandedClaimId, setExpandedClaimId] = useState<string | null>(null);
  const [claimLinesById, setClaimLinesById] = useState<Record<string, ClaimLineDetail[]>>({});
  const [claimLinesLoadingId, setClaimLinesLoadingId] = useState<string | null>(null);
  const [claimLinesErrorId, setClaimLinesErrorId] = useState<string | null>(null);
  const [deletingClaimId, setDeletingClaimId] = useState<string | null>(null);
  const [claimStatusModal, setClaimStatusModal] = useState<ClaimDocRow | null>(null);
  const [claimStatusChoice, setClaimStatusChoice] = useState<"SUBMITTED" | "SETTLED">("SUBMITTED");
  const [claimStatusModalSaving, setClaimStatusModalSaving] = useState(false);

  const selectedRows = useMemo(() => rows.filter((r) => selectedIds[r.id]), [rows, selectedIds]);
  const selectableRows = useMemo(
    () => rows.filter((r) => Number(r.outstandingQty) > 0),
    [rows]
  );
  const allSelectableSelected = useMemo(
    () => selectableRows.length > 0 && selectableRows.every((r) => selectedIds[r.id]),
    [selectableRows, selectedIds]
  );
  const someSelectableSelected = useMemo(
    () => selectableRows.some((r) => selectedIds[r.id]) && !allSelectableSelected,
    [selectableRows, selectedIds, allSelectableSelected]
  );
  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      if (allSelectableSelected) {
        for (const r of selectableRows) delete next[r.id];
      } else {
        for (const r of selectableRows) next[r.id] = true;
      }
      return next;
    });
  }, [allSelectableSelected, selectableRows]);

  const selectedOutstandingTotal = useMemo(
    () => selectedRows.reduce((sum, r) => sum + Number(r.outstandingAmount || 0), 0),
    [selectedRows]
  );
  const selectedClaimQtyTotal = useMemo(
    () =>
      selectedRows.reduce((sum, r) => {
        const q = Number(claimQtyById[r.id] || 0);
        return sum + (Number.isFinite(q) ? Math.max(Math.floor(q), 0) : 0);
      }, 0),
    [selectedRows, claimQtyById]
  );
  const selectedClaimAmountTotal = useMemo(
    () =>
      selectedRows.reduce((sum, r) => {
        const q = Number(claimQtyById[r.id] || 0);
        const qty = Number.isFinite(q) ? Math.max(Math.floor(q), 0) : 0;
        const rate = Number(r.rebatePerUnitSnapshot || 0);
        return sum + qty * (Number.isFinite(rate) ? rate : 0);
      }, 0),
    [selectedRows, claimQtyById]
  );

  /** Sums for Accrued / Claimed / Outstanding on the current table page only. */
  const ledgerPageTotals = useMemo(() => {
    let accrued = 0;
    let claimed = 0;
    let outstanding = 0;
    for (const r of rows) {
      accrued += Number(r.rebateTotalSnapshot || 0);
      claimed += Number(r.claimedAmount || 0);
      outstanding += Number(r.outstandingAmount || 0);
    }
    return { accrued, claimed, outstanding };
  }, [rows]);

  /** When a distributor is selected, only teams in that distributor's supplyTo (Stocks Supply To). */
  const teamsForFilter = useMemo(() => {
    if (!distributorId) return teams;
    const distributor = distributors.find((d) => d.id === distributorId);
    if (!distributor) return teams;
    const supplyNames = new Set(
      (distributor.supplyTo || []).map((name) => String(name).trim()).filter(Boolean)
    );
    return teams.filter((t) => supplyNames.has((t.name || "").trim()));
  }, [distributorId, distributors, teams]);

  useEffect(() => {
    if (!teamId) return;
    if (!teamsForFilter.some((t) => t.id === teamId)) setTeamId("");
  }, [teamId, teamsForFilter]);

  /** All products checked = equivalent to "All Products" (no filter). */
  const isAllProductsSelected = products.length > 0 && selectedProductIds.length === products.length;
  /** User explicitly unchecked every product (distinct from the initial/all-selected state). */
  const noProductsSelected = productFilterInitialized && products.length > 0 && selectedProductIds.length === 0;
  const productFilterLabel = useMemo(() => {
    if (products.length === 0) return "All Products";
    if (isAllProductsSelected) return "All Products";
    if (selectedProductIds.length === 0) return "No Products Selected";
    if (selectedProductIds.length === 1) {
      const p = products.find((x) => x.id === selectedProductIds[0]);
      return p ? p.name : "1 Product";
    }
    return `${selectedProductIds.length} Products Selected`;
  }, [products, isAllProductsSelected, selectedProductIds]);

  const loadBaseData = async () => {
    const [dRes, tRes, pRes] = await Promise.all([
      axios.get(`${apiUrl}/distributors?pageSize=500`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${apiUrl}/teams?pageSize=500`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${apiUrl}/products?pageSize=500`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    setDistributors(Array.isArray(dRes.data?.data) ? dRes.data.data : []);
    const teamList: Team[] = Array.isArray(tRes.data?.data) ? tRes.data.data : [];
    setTeams(teamList.filter((t) => t.status !== false).sort((a, b) => a.name.localeCompare(b.name)));
    const productList: Product[] = Array.isArray(pRes.data?.data) ? pRes.data.data : [];
    setProducts(productList);

    // Restore the last-remembered product selection. Nothing stored yet (first visit) defaults
    // to "all selected" (no filter); an explicitly-empty stored selection is remembered as-is.
    const stored = loadStoredProductIds();
    if (stored === null) {
      setSelectedProductIds(productList.map((p) => p.id));
    } else {
      const validIds = new Set(productList.map((p) => p.id));
      setSelectedProductIds(stored.filter((id) => validIds.has(id)));
    }
    setProductFilterInitialized(true);
  };

  // Remember the product filter selection so it persists across visits/reloads.
  useEffect(() => {
    if (!productFilterInitialized) return;
    saveStoredProductIds(selectedProductIds);
  }, [selectedProductIds, productFilterInitialized]);

  const loadLedger = async () => {
    setLoading(true);
    setError("");
    try {
      const parsedRange = parseDateRangeInput(dateRange);
      if (!parsedRange) {
        setError("Please select a valid date range.");
        setRows([]);
        setTotal(0);
        setLoading(false);
        return;
      }
      if (noProductsSelected) {
        setRows([]);
        setTotal(0);
        setSelectedIds({});
        setClaimQtyById({});
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));
      if (distributorId) params.set("distributorId", distributorId);
      if (teamId) params.set("teamId", teamId);
      if (!isAllProductsSelected && selectedProductIds.length > 0) {
        params.set("productId", selectedProductIds.join(","));
      }
      if (status) params.set("status", status);
      params.set("startDate", parsedRange.startDate);
      params.set("endDate", parsedRange.endDate);
      const res = await axios.get(`${apiUrl}/rebates/ledger?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(data);
      setTotal(Number(res.data?.total || 0));
      setSelectedIds({});
      const nextClaimQty: Record<string, string> = {};
      for (const r of data) nextClaimQty[r.id] = String(Math.max(Number(r.outstandingQty || 0), 0));
      setClaimQtyById(nextClaimQty);
    } catch {
      setError("Failed to load rebate ledger.");
    } finally {
      setLoading(false);
    }
  };

  const loadClaimDocs = async (opts?: { preserveExpanded?: boolean }) => {
    const preserveExpanded = opts?.preserveExpanded ?? false;
    try {
      const parsedRange = parseDateRangeInput(dateRange);
      if (!parsedRange) return;
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("pageSize", "200");
      if (distributorId) params.set("distributorId", distributorId);
      params.set("startDate", parsedRange.startDate);
      params.set("endDate", parsedRange.endDate);
      const res = await axios.get(`${apiUrl}/rebates/claims?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setClaimDocs(data);
      if (!preserveExpanded) {
        setExpandedClaimId(null);
        setClaimLinesById({});
        setClaimLinesLoadingId(null);
        setClaimLinesErrorId(null);
      }
    } catch {
      // keep ledger usable
    }
  };

  const handleClaimRowToggle = useCallback(
    async (claimId: string) => {
      if (expandedClaimId === claimId) {
        setExpandedClaimId(null);
        return;
      }
      setExpandedClaimId(claimId);
      if (claimId in claimLinesById) return;

      setClaimLinesLoadingId(claimId);
      setClaimLinesErrorId(null);
      try {
        const res = await axios.get(`${apiUrl}/rebates/claims/${claimId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const lines = Array.isArray(res.data?.data?.lines) ? res.data.data.lines : [];
        setClaimLinesById((prev) => ({ ...prev, [claimId]: lines }));
      } catch {
        setClaimLinesErrorId(claimId);
      } finally {
        setClaimLinesLoadingId(null);
      }
    },
    [apiUrl, token, expandedClaimId, claimLinesById]
  );

  const handleDownloadClaimExcel = useCallback(
    async (e: React.MouseEvent, c: ClaimDocRow) => {
      e.stopPropagation();
      setError("");
      try {
        const res = await axios.get(`${apiUrl}/rebates/claims/${c.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = res.data?.data as ClaimDetailPayload | undefined;
        if (!d || typeof d.claimRef !== "string") {
          setError("Could not load claim for export.");
          return;
        }
        let lines = Array.isArray(d.lines) ? d.lines : [];
        const cachedLines = claimLinesById[c.id];
        if (!lines.length && cachedLines?.length) {
          lines = cachedLines;
        }
        const claimDateStr =
          typeof d.claimDate === "string" ? d.claimDate.slice(0, 10) : String(d.claimDate ?? "");

        // One sheet: header block + same columns as expanded row in the UI (product, SKU, stock-in, qty, amounts).
        const aoa: (string | number)[][] = [
          ["Claim Ref", d.claimRef],
          ["Claim Date", claimDateStr],
          ["Distributor", d.distributor?.name ?? ""],
          ["Status", d.status],
          ["Notes", d.notes ?? ""],
          [],
          ["Claim total (RM)", Number(c.totalClaimedAmount || 0)],
          ["Settled (RM)", Number(c.settledAmount || 0)],
          ["Unsettled on claim (RM)", Number(c.outstandingAmount || 0)],
          [],
          ["Line items"],
          [],
          ["Product", "SKU", "Stock In", "Accrual Qty", "Claim Qty", "Rate (RM)", "Total Claimed (RM)"],
        ];

        if (lines.length) {
          for (const line of lines) {
            aoa.push([
              line.product?.name ?? "",
              line.product?.sku ?? "",
              line.stockInReceipt?.batchNo ?? "",
              line.accrualQty,
              line.claimedQty,
              Number(line.rebatePerUnit),
              Number(line.claimedAmount),
            ]);
          }
        } else {
          aoa.push(["(No line items for this claim.)", "", "", "", "", "", ""]);
        }

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Claim");
        XLSX.writeFile(wb, `${safeClaimExportBasename(d.claimRef)}-claim.xlsx`);
      } catch {
        setError("Failed to download claim Excel.");
      }
    },
    [apiUrl, token, claimLinesById]
  );

  const handleSaveClaimPaymentStatus = useCallback(async () => {
    const c = claimStatusModal;
    if (!c) return;
    const choice = claimStatusChoice;
    const action = choice === "SETTLED" ? "MARK_SETTLED" : "MARK_SUBMITTED";
    setError("");
    setSuccess("");
    setClaimStatusModalSaving(true);
    try {
      const res = await axios.patch(
        `${apiUrl}/rebates/claims/${c.id}/workflow`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data?.data as { status?: string; settledAmount?: string | number } | undefined;
      const total = parseMoneyAmount(c.totalClaimedAmount);
      const nextStatus = typeof data?.status === "string" ? data.status : c.status;
      const nextSettled =
        data?.settledAmount !== undefined ? parseMoneyAmount(data.settledAmount) : parseMoneyAmount(c.settledAmount);
      const nextOutstanding = Math.max(0, total - nextSettled);
      setClaimDocs((prev) =>
        prev.map((x) =>
          x.id === c.id
            ? {
                ...x,
                status: nextStatus,
                settledAmount: to2(nextSettled),
                outstandingAmount: to2(nextOutstanding),
              }
            : x
        )
      );
      setClaimStatusModal(null);
      setSuccess(
        choice === "SETTLED"
          ? `Claim ${c.claimRef} marked as paid.`
          : `Claim ${c.claimRef} set back to submitted (unsettled).`
      );
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? err.response?.data?.message : null;
      setError(typeof msg === "string" ? msg : "Failed to update claim payment status.");
    } finally {
      setClaimStatusModalSaving(false);
    }
  }, [apiUrl, token, claimStatusModal, claimStatusChoice]);

  const handleDeleteClaim = useCallback(
    async (e: React.MouseEvent, c: ClaimDocRow) => {
      e.stopPropagation();
      if (!claimRowAllowsDelete(c)) return;
      const confirmMsg = [
        `Permanently delete claim ${c.claimRef}?`,
        "",
        "This removes the claim from the system.",
        "",
        "THIS ACTION CANNOT BE REVERSED.",
        "",
        "Click OK to delete, or Cancel to keep the claim.",
      ].join("\n");
      if (!window.confirm(confirmMsg)) return;
      setError("");
      setSuccess("");
      setDeletingClaimId(c.id);
      try {
        await axios.delete(`${apiUrl}/rebates/claims/${c.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClaimDocs((prev) => prev.filter((x) => x.id !== c.id));
        if (expandedClaimId === c.id) setExpandedClaimId(null);
        setClaimLinesById((prev) => {
          const next = { ...prev };
          delete next[c.id];
          return next;
        });
        setSuccess(
          "Claim deleted. Rebate ledger claimed quantities and amounts for the affected lines have been restored (excluding any other claims)."
        );
        await loadLedger();
        await loadClaimDocs({ preserveExpanded: true });
      } catch (err: unknown) {
        const msg = axios.isAxiosError(err) ? err.response?.data?.message : null;
        setError(typeof msg === "string" ? msg : "Failed to delete claim.");
      } finally {
        setDeletingClaimId(null);
      }
    },
    [apiUrl, token, expandedClaimId, loadLedger, loadClaimDocs]
  );

  useEffect(() => {
    void loadBaseData();
  }, []);

  useEffect(() => {
    void loadLedger();
    void loadClaimDocs();
  }, [page, pageSize]);

  // Re-run once the remembered product selection has been restored, so the initial view
  // (before the user clicks Filter) already reflects it instead of showing everything.
  useEffect(() => {
    if (!productFilterInitialized) return;
    void loadLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFilterInitialized]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSearch = () => {
    setPage(1);
    void loadLedger();
    void loadClaimDocs();
  };

  const handleExport = async () => {
    try {
      const parsedRange = parseDateRangeInput(dateRange);
      if (!parsedRange) {
        setError("Please select a valid date range.");
        return;
      }
      if (noProductsSelected) {
        setError("Select at least one product to export.");
        return;
      }
      const params = new URLSearchParams();
      if (distributorId) params.set("distributorId", distributorId);
      if (teamId) params.set("teamId", teamId);
      if (!isAllProductsSelected && selectedProductIds.length > 0) {
        params.set("productId", selectedProductIds.join(","));
      }
      if (status) params.set("status", status);
      params.set("startDate", parsedRange.startDate);
      params.set("endDate", parsedRange.endDate);

      const res = await axios.get(`${apiUrl}/rebates/ledger/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rebate-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to export ledger.");
    }
  };

  const handleCreateClaim = async () => {
    if (!selectedRows.length) {
      setError("Select at least one ledger row.");
      return;
    }
    for (const r of selectedRows) {
      const q = Math.floor(Number(claimQtyById[r.id] || 0));
      if (!Number.isFinite(q) || q <= 0) {
        setError(`Claim qty must be > 0 for ${r.product.name}.`);
        return;
      }
      if (q > Number(r.outstandingQty || 0)) {
        setError(`Claim qty exceeds balance qty for ${r.product.name}.`);
        return;
      }
    }
    const distinctDistributors = Array.from(new Set(selectedRows.map((r) => r.distributor.id)));
    if (distinctDistributors.length !== 1) {
      setError("Please select rows from one distributor only.");
      return;
    }
    setClaiming(true);
    setError("");
    setSuccess("");
    try {
      await axios.post(
        `${apiUrl}/rebates/claims`,
        {
          claimRef: claimRef.trim() || undefined,
          claimDate,
          distributorId: distinctDistributors[0],
          notes: claimNote,
          items: selectedRows.map((r) => ({
            rebateAccrualId: r.id,
            claimedQty: Math.floor(Number(claimQtyById[r.id] || 0)),
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess("Claim created successfully.");
      setClaimRef("");
      setClaimNote("");
      setSelectedIds({});
      await loadLedger();
      await loadClaimDocs();
    } catch {
      setError("Failed to create claim.");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="p-6">
          <div className="flex items-center gap-2">
            {loading ? <Lucide icon="Loader2" className="w-4 h-4 animate-spin text-slate-500" /> : null}
          </div>

          <div className="box p-4 mt-4 grid grid-cols-1 md:grid-cols-7 gap-3">
            <div className="md:col-span-7">
              <StepHeading step={1}>Filter</StepHeading>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">
                Distributor
              </div>
              <FormSelect value={distributorId} onChange={(e) => setDistributorId(e.target.value)}>
                <option value="">All Distributors</option>
                {distributors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </FormSelect>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">
                Team
              </div>
              <FormSelect value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                <option value="">All Teams</option>
                {teamsForFilter.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </FormSelect>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">
                Product
              </div>
              <Popover className="w-full">
                <Popover.Button
                  as="button"
                  type="button"
                  className="w-full flex items-center justify-between gap-2 rounded-[0.5rem] border border-slate-300/80 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 px-3 h-[38px] text-sm text-left"
                >
                  <span className="truncate">{productFilterLabel}</span>
                  <Lucide icon="ChevronDown" className="w-4 h-4 shrink-0 text-slate-500" />
                </Popover.Button>
                <Popover.Panel placement="bottom-start" className="w-72 p-0 overflow-hidden">
                  {({ close }: { close: () => void }) => (
                    <>
                      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200/70 dark:border-darkmode-400">
                        <button
                          type="button"
                          className="text-xs font-medium text-primary hover:underline"
                          onClick={() => setSelectedProductIds(products.map((p) => p.id))}
                        >
                          Select All
                        </button>
                        <span className="text-slate-300 dark:text-darkmode-300">|</span>
                        <button
                          type="button"
                          className="text-xs font-medium text-primary hover:underline"
                          onClick={() => setSelectedProductIds([])}
                        >
                          Deselect All
                        </button>
                        <button
                          type="button"
                          className="ml-auto text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                          onClick={() => close()}
                        >
                          Done
                        </button>
                      </div>
                      <div className="max-h-64 overflow-y-auto py-1">
                        {products.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-slate-500">No products found.</div>
                        ) : (
                          products.map((p) => {
                            const checked = selectedProductIds.includes(p.id);
                            return (
                              <label
                                key={p.id}
                                className="flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-500"
                              >
                                <input
                                  type="checkbox"
                                  className="rounded border-slate-300"
                                  checked={checked}
                                  onChange={() => {
                                    setSelectedProductIds((prev) =>
                                      prev.includes(p.id) ? prev.filter((id) => id !== p.id) : [...prev, p.id]
                                    );
                                  }}
                                />
                                <span className="truncate flex-1">
                                  {p.name} {p.sku ? `(${p.sku})` : ""}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}
                </Popover.Panel>
              </Popover>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">
                Accrual Status
              </div>
              <FormSelect value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="ACCRUED">ACCRUED</option>
                <option value="PARTIALLY_CLAIMED">PARTIALLY_CLAIMED</option>
                <option value="CLAIMED">CLAIMED</option>
              </FormSelect>
            </div>
            <div className="md:col-span-2">
              <div className="text-xs text-slate-500 mb-1">
                Date Range
              </div>
              <div className="relative inline-flex w-full">
                <Lucide
                  icon="Calendar"
                  className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                />
                <Litepicker
                  value={dateRange}
                  onChange={(e) => setDateRange((e.target?.value || "").trim())}
                  options={{
                    autoApply: true,
                    singleMode: false,
                    numberOfColumns: 2,
                    numberOfMonths: 2,
                    showWeekNumbers: true,
                    dropdowns: {
                      minYear: 2020,
                      maxYear: null,
                      months: true,
                      years: true,
                    },
                    format: "YYYY-MM-DD",
                  }}
                  className="w-full min-w-[220px] pl-9 pr-3 py-2 rounded-[0.5rem] text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 md:items-end">
              <Button variant="primary" onClick={handleSearch}>
                Filter
              </Button>
              <Button variant="outline-secondary" onClick={handleExport}>
                Export Excel
              </Button>
            </div>
          </div>

          {error ? <div className="mt-4 text-danger text-sm">{error}</div> : null}
          {success ? <div className="mt-4 text-success text-sm">{success}</div> : null}

          <div className="box mt-4 overflow-x-auto">
            <div className="px-4 pt-4 pb-1">
              <StepHeading step={2}>Select Products & Quantity to Claim</StepHeading>
            </div>
            <Table className="border-b border-slate-200/60 text-xs table-auto w-max min-w-full">
              <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                <Table.Tr>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[48px] text-center">
                    <input
                      type="checkbox"
                      aria-label="Select all claimable rows on this page"
                      title="Select all on this page"
                      checked={allSelectableSelected}
                      disabled={selectableRows.length === 0}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelectableSelected;
                      }}
                      onChange={handleToggleSelectAll}
                    />
                  </Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[110px]">Date</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[170px]">Team</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[190px]">Product</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-success/10 dark:bg-success/10 min-w-[70px] text-right">Qty</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-primary/10 dark:bg-primary/10 min-w-[110px] text-center">Claim Qty</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-success/10 dark:bg-success/10 min-w-[90px] text-right">Rebate Rate</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[120px] text-right">Trade-In</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[100px] text-right">Accrued</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[100px] text-right">Claimed</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[110px] text-right">Outstanding</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[140px]">Status</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[120px]">Claimed Inv No</Table.Td>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) => (
                  <Table.Tr key={r.id} className="[&_td]:border-b [&_td]:border-slate-200/60">
                    <Table.Td className="py-2 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={!!selectedIds[r.id]}
                        disabled={Number(r.outstandingQty) <= 0}
                        onChange={(e) => setSelectedIds((prev) => ({ ...prev, [r.id]: e.target.checked }))}
                      />
                    </Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{fmtDate(r.accrualDate)}</Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{r.team?.name || "—"}</Table.Td>
                    <Table.Td className="py-2 px-2 max-w-[300px] truncate">{r.product.name}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-success/10 dark:bg-success/10">{r.qty}</Table.Td>
                    <Table.Td className="py-2 px-2 text-center tabular-nums bg-primary/10 dark:bg-primary/10">
                      <FormInput
                        type="number"
                        min={0}
                        step={1}
                        className="w-[88px] text-center h-7 text-xs"
                        value={claimQtyById[r.id] ?? String(r.outstandingQty ?? 0)}
                        disabled={!selectedIds[r.id] || Number(r.outstandingQty) <= 0}
                        onChange={(e) =>
                          setClaimQtyById((prev) => ({
                            ...prev,
                            [r.id]: e.target.value.replace(/[^\d]/g, ""),
                          }))
                        }
                        onBlur={(e) => {
                          const raw = Number(e.target.value || 0);
                          const normalized = Math.max(0, Math.min(Math.floor(raw), Number(r.outstandingQty || 0)));
                          setClaimQtyById((prev) => ({ ...prev, [r.id]: String(normalized) }));
                        }}
                      />
                    </Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-success/10 dark:bg-success/10">{to2(r.rebatePerUnitSnapshot)}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-slate-50 dark:bg-darkmode-700">{r.tradeInDevice ?? "—"}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(r.rebateTotalSnapshot)}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(r.claimedAmount)}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(r.outstandingAmount)}</Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{r.status}</Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{r.stockInReceipt?.invoiceNo || "—"}</Table.Td>
                  </Table.Tr>
                ))}
                {rows.length > 0 ? (
                  <Table.Tr className="border-t-2 border-slate-300/80 bg-slate-50/90 dark:bg-darkmode-700/50 font-medium">
                    <Table.Td colSpan={8} className="py-2 px-2" />
                    <Table.Td className="py-2 px-2 tabular-nums bg-warning/15 dark:bg-warning/10 text-sm text-slate-900 dark:text-slate-100 whitespace-nowrap">
                      <div className="w-full text-end">{to2Comma(ledgerPageTotals.accrued)}</div>
                    </Table.Td>
                    <Table.Td className="py-2 px-2 tabular-nums bg-warning/15 dark:bg-warning/10 text-sm text-slate-900 dark:text-slate-100 whitespace-nowrap">
                      <div className="w-full text-end">{to2Comma(ledgerPageTotals.claimed)}</div>
                    </Table.Td>
                    <Table.Td className="py-2 px-2 tabular-nums bg-warning/15 dark:bg-warning/10 text-sm text-slate-900 dark:text-slate-100 whitespace-nowrap">
                      <div className="w-full text-end">{to2Comma(ledgerPageTotals.outstanding)}</div>
                    </Table.Td>
                    <Table.Td colSpan={2} className="py-2 px-2" />
                  </Table.Tr>
                ) : null}
                {!rows.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={13} className="text-center text-slate-500 py-6">
                      No ledger rows found. Try a wider Date Range and click Filter.
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          </div>

          <div className="box p-4 mt-4">
            <StepHeading step={3} className="mb-1">
              Create Claim
            </StepHeading>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <div className="text-xs text-slate-500 mb-1">
                  Claimed Inv No:
                </div>
                <FormInput
                  type="text"
                  placeholder="Claimed Inv No (optional)"
                  value={claimRef}
                  onChange={(e) => setClaimRef(e.target.value)}
                  className="w-56"
                />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1 whitespace-nowrap">
                  Claim Date
                </div>
                <div className="relative inline-flex w-44">
                  <Lucide
                    icon="Calendar"
                    className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                  />
                  <Litepicker
                    value={claimDate}
                    onChange={(e) => setClaimDate((e.target?.value || "").trim())}
                    options={{
                      autoApply: true,
                      singleMode: true,
                      numberOfColumns: 1,
                      numberOfMonths: 1,
                      dropdowns: {
                        minYear: 2020,
                        maxYear: null,
                        months: true,
                        years: true,
                      },
                      format: "YYYY-MM-DD",
                    }}
                    className="w-full pl-9 pr-3 py-2 rounded-[0.5rem] text-sm"
                  />
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">
                  Claim Note
                </div>
                <FormInput
                  type="text"
                  placeholder="Claim note"
                  value={claimNote}
                  onChange={(e) => setClaimNote(e.target.value)}
                  className="w-64"
                />
              </div>
              <Button variant="success" className="self-end" onClick={handleCreateClaim} disabled={claiming || !selectedRows.length}>
                Mark Selected as Claimed
              </Button>
              <div className="text-sm text-slate-500 self-end pb-1">
                Selected: {selectedRows.length} • Claim Qty: {selectedClaimQtyTotal} • Claim Amount: RM {to2(selectedClaimAmountTotal)} • Total Outstanding: RM {to2(selectedOutstandingTotal)}
              </div>
            </div>
          </div>

          <div className="box mt-4 overflow-x-auto">
            <div className="px-4 pt-4 pb-1">
              <StepHeading step={4}>Claim Report History/Archive</StepHeading>
            </div>
            <p className="px-4 pb-2 text-xs text-slate-500">
              Click a claim row to expand and see each product and quantity included in that claim.
            </p>
            <Table className="border-b border-slate-200/60 text-xs table-auto w-max min-w-full">
              <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                <Table.Tr>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[160px]">Claim Ref</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[110px]">Claim Date</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[170px]">Distributor</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[120px] text-right">
                    Claim total (RM)
                  </Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[110px] text-right">
                    Settled (RM)
                  </Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[120px] text-right">
                    Unsettled (RM)
                  </Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[150px]">Status</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-white dark:bg-darkmode-600 min-w-[100px] whitespace-nowrap sticky right-0 z-[2] border-l border-slate-200/80 dark:border-darkmode-400 shadow-[-8px_0_16px_-8px_rgba(15,23,42,0.12)]">
                    Action
                  </Table.Td>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {claimDocs.map((c) => (
                  <Fragment key={c.id}>
                    <Table.Tr
                      className={`[&_td]:border-b [&_td]:border-slate-200/60 cursor-pointer transition-colors hover:bg-slate-50/90 dark:hover:bg-darkmode-400/30 ${
                        expandedClaimId === c.id ? "bg-slate-50/80 dark:bg-darkmode-600/40" : ""
                      }`}
                      onClick={() => void handleClaimRowToggle(c.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          void handleClaimRowToggle(c.id);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-expanded={expandedClaimId === c.id}
                    >
                      <Table.Td className="py-2 px-2 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          <Lucide
                            icon={expandedClaimId === c.id ? "ChevronDown" : "ChevronRight"}
                            className="w-3.5 h-3.5 shrink-0 text-slate-400"
                          />
                          <span className="font-medium text-slate-800 dark:text-slate-200">{c.claimRef}</span>
                        </span>
                      </Table.Td>
                      <Table.Td className="py-2 px-2 whitespace-nowrap">{fmtDate(c.claimDate)}</Table.Td>
                      <Table.Td className="py-2 px-2 whitespace-nowrap">{c.distributor.name}</Table.Td>
                      <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(c.totalClaimedAmount)}</Table.Td>
                      <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(c.settledAmount)}</Table.Td>
                      <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(c.outstandingAmount)}</Table.Td>
                      <Table.Td className="py-2 px-2 whitespace-nowrap">{formatClaimDocStatus(c.status)}</Table.Td>
                      <Table.Td
                        className="py-2 px-2 whitespace-nowrap sticky right-0 z-[1] bg-white dark:bg-darkmode-600 border-l border-slate-200/80 dark:border-darkmode-400 shadow-[-8px_0_16px_-8px_rgba(15,23,42,0.12)]"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <div className="inline-flex items-center gap-1.5">
                          {claimRowAllowsPaymentStatusModal(c) ? (
                            <button
                              type="button"
                              className="btn btn-outline-primary p-2 rounded-lg"
                              onClick={(e) => {
                                e.stopPropagation();
                                setClaimStatusModal(c);
                                setClaimStatusChoice(c.status === "SETTLED" ? "SETTLED" : "SUBMITTED");
                              }}
                            >
                              <Lucide icon="FileText" className="w-3.5 h-3.5 stroke-[1.3]" />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn-outline-secondary p-2 rounded-lg"
                            onClick={(e) => void handleDownloadClaimExcel(e, c)}
                          >
                            <Lucide icon="Download" className="w-3.5 h-3.5 stroke-[1.3]" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger p-2 rounded-lg disabled:opacity-40"
                            disabled={!claimRowAllowsDelete(c) || deletingClaimId === c.id}
                            onClick={(e) => void handleDeleteClaim(e, c)}
                          >
                            {deletingClaimId === c.id ? (
                              <Lucide icon="Loader2" className="w-3.5 h-3.5 stroke-[1.3] animate-spin" />
                            ) : (
                              <Lucide icon="Trash2" className="w-3.5 h-3.5 stroke-[1.3]" />
                            )}
                          </button>
                        </div>
                      </Table.Td>
                    </Table.Tr>
                    {expandedClaimId === c.id ? (
                      <Table.Tr className="bg-slate-50/70 dark:bg-darkmode-600/25">
                        <Table.Td colSpan={8} className="p-0 border-b border-slate-200/60">
                          <div className="px-4 py-3">
                            {claimLinesLoadingId === c.id ? (
                              <div className="flex items-center gap-2 text-slate-500 py-2">
                                <Lucide icon="Loader2" className="w-4 h-4 animate-spin" />
                                Loading lines…
                              </div>
                            ) : claimLinesErrorId === c.id ? (
                              <div className="text-danger text-sm py-2">Could not load claim lines.</div>
                            ) : (claimLinesById[c.id]?.length ?? 0) === 0 ? (
                              <div className="text-slate-500 text-sm py-2">No line items for this claim.</div>
                            ) : (
                              <table className="w-full text-left text-xs border border-slate-200/80 dark:border-darkmode-400 rounded-md overflow-hidden">
                                <thead>
                                  <tr className="bg-slate-100/90 dark:bg-darkmode-700 text-slate-600 dark:text-slate-300">
                                    <th className="py-2 px-3 font-medium">Product</th>
                                    <th className="py-2 px-3 font-medium w-28">SKU</th>
                                    <th className="py-2 px-3 font-medium w-32">Team</th>
                                    <th className="py-2 px-3 font-medium text-right w-24">Accrual Qty</th>
                                    <th className="py-2 px-3 font-medium text-right w-24">Claim Qty</th>
                                    <th className="py-2 px-3 font-medium text-right w-28">Rate (RM)</th>
                                    <th className="py-2 px-3 font-medium text-right w-28">Total Claimed (RM)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(claimLinesById[c.id] || []).map((line) => (
                                    <tr key={line.id} className="border-t border-slate-200/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-600">
                                      <td className="py-2 px-3 max-w-[240px] truncate">{line.product.name}</td>
                                      <td className="py-2 px-3 whitespace-nowrap text-slate-600 dark:text-slate-400">
                                        {line.product.sku || "—"}
                                      </td>
                                      <td className="py-2 px-3 whitespace-nowrap">{line.team?.name || "—"}</td>
                                      <td className="py-2 px-3 text-right tabular-nums">{line.accrualQty}</td>
                                      <td className="py-2 px-3 text-right tabular-nums font-medium">{line.claimedQty}</td>
                                      <td className="py-2 px-3 text-right tabular-nums">{to2(line.rebatePerUnit)}</td>
                                      <td className="py-2 px-3 text-right tabular-nums">{to2(line.claimedAmount)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </Table.Td>
                      </Table.Tr>
                    ) : null}
                  </Fragment>
                ))}
                {!claimDocs.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={8} className="text-center text-slate-500 py-6">
                      No claim documents found for selected filters/date range.
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <Pagination>
              <Pagination.Link onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <Lucide icon="ChevronLeft" className="w-4 h-4" />
              </Pagination.Link>
              <Pagination.Link active>{page}</Pagination.Link>
              <Pagination.Link onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                <Lucide icon="ChevronRight" className="w-4 h-4" />
              </Pagination.Link>
            </Pagination>
            <FormSelect
              className="w-24"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </FormSelect>
            <div className="text-sm text-slate-500">Total: {total}</div>
          </div>
        </div>

        <Dialog
          open={claimStatusModal !== null}
          onClose={() => {
            if (claimStatusModalSaving) return;
            setClaimStatusModal(null);
          }}
        >
          <Dialog.Panel className="max-w-md">
            <Dialog.Title>
              <div className="font-medium">Payment status</div>
            </Dialog.Title>
            <Dialog.Description className="space-y-4">
              {claimStatusModal ? (
                <>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Claim <span className="font-medium text-slate-800 dark:text-slate-100">{claimStatusModal.claimRef}</span>
                    <span className="text-slate-500"> · </span>
                    RM {to2(claimStatusModal.totalClaimedAmount)} total
                  </p>
                  <p className="text-xs text-slate-500">Choose how this claim should appear for settlement tracking.</p>
                  <div className="space-y-3 text-sm">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="claimPaymentStatus"
                        className="rounded-full border-slate-300 text-primary focus:ring-primary/30"
                        checked={claimStatusChoice === "SUBMITTED"}
                        onChange={() => setClaimStatusChoice("SUBMITTED")}
                        disabled={claimStatusModalSaving}
                      />
                      <span className="font-medium text-slate-800 dark:text-slate-100">Submitted</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="claimPaymentStatus"
                        className="rounded-full border-slate-300 text-primary focus:ring-primary/30"
                        checked={claimStatusChoice === "SETTLED"}
                        onChange={() => setClaimStatusChoice("SETTLED")}
                        disabled={claimStatusModalSaving}
                      />
                      <span className="font-medium text-slate-800 dark:text-slate-100">Paid</span>
                    </label>
                  </div>
                </>
              ) : null}
            </Dialog.Description>
            <Dialog.Footer>
              <Button
                variant="outline-secondary"
                type="button"
                disabled={claimStatusModalSaving}
                onClick={() => {
                  if (claimStatusModalSaving) return;
                  setClaimStatusModal(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="button"
                disabled={claimStatusModalSaving || !claimStatusModal}
                onClick={() => void handleSaveClaimPaymentStatus()}
              >
                {claimStatusModalSaving ? (
                  <span className="inline-flex items-center gap-2">
                    <Lucide icon="Loader2" className="w-4 h-4 animate-spin" />
                    Saving…
                  </span>
                ) : (
                  "Save"
                )}
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
      </RoleGate>
    </ProtectedRoute>
  );
}

